import React, { ChangeEvent, FC, useState } from 'react'
import { Icon } from '@iconify/react'
import clsx from 'clsx'

import { formatBytesToHumanReadableString } from '@pepper/utils/src/function/misc'
import { Avatar, TextArea, Tooltip, Button } from '../../../atoms'

import FileUpload from '../FileUpload/FileUpload'

import styles from './_.module.css'

interface ExtendedAttachments extends Attachment {
  status?: string
  size: number
}
interface Props {
  className?: string
  avatar?: string | null
  onSubmit?: () => void
  onChange: (values: CommentBox) => void
  formData?: {
    comment: string
    attachments: ExtendedAttachments[]
  }
  showSendButton?: boolean
  allowedFiles?: string[]
  placeholder?: string
  autoSize?: {
    minRows: number
    maxRows: number
  }
  disabled?: boolean
  showCount?: boolean
  maxLength?: number
  showGroup?: boolean
  updatePosition?: (val: number) => void
}

export const CommentBoxV2: FC<Props> = ({
  avatar,
  onSubmit,
  onChange,
  formData = {
    comment: '',
    attachments: []
  },
  showSendButton = true,
  className,
  placeholder = 'Add a comment...',
  autoSize = {
    minRows: 0,
    maxRows: 5
  },
  disabled = false,
  allowedFiles,
  showCount = false,
  maxLength = undefined,
  showGroup,
  updatePosition
}) => {
  const [isFiledFocused, toggleFiledFocused] = useState<boolean>(false)

  const handleCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    let { value } = event.target
    value = value.trim() !== '' ? value : ''
    onChange({ ...formData, comment: value })
  }

  const isFormValid: () => boolean = () => {
    return formData.comment || formData.attachments?.length > 0 ? true : false
  }

  const removeFile = (index: number): void => {
    const _attachments = formData.attachments?.filter((_, i) => i !== index)
    onChange({ ...formData, attachments: [..._attachments] })
  }

  const getFileActionButton = (file: ExtendedAttachments, index: number) => {
    if (file.status === 'uploading')
      return (
        <Button
          icon={
            <Icon icon="ant-design:loading-outlined" width="18" height="18" />
          }
          block={true}
          disabled={disabled}
        />
      )
    else if (file.status === 'error')
      return (
        <Button
          className={styles.closeBtn}
          icon={
            <Icon
              icon="lucide:alert-circle"
              width="18"
              height="18"
              color="red"
            />
          }
          onClick={() => removeFile(index)}
          block={true}
          disabled={disabled}
        />
      )
    else
      return (
        <Button
          className={styles.closeBtn}
          onClick={() => removeFile(index)}
          icon={
            <Icon
              icon="lucide:x"
              width="18"
              height="18"
              color="var(--color-gray-900)"
            />
          }
          block={true}
          disabled={disabled}
        />
      )
  }
  return (
    <div
      className={`${styles.addCommentContainer} ${
        isFormValid() || isFiledFocused ? styles.validForm : ''
      } ${className}`}
    >
      {avatar ? (
        <div className={styles.profilePic}>
          <Avatar
            className={styles.avatar}
            icon={<Icon icon="lucide:user" />}
            src={avatar ?? ''}
          />
        </div>
      ) : (
        ''
      )}
      <div
        className={`${styles.addComments} ${
          formData.attachments?.length > 0 ? styles.textWithAttachments : ''
        }`}
      >
        <div className={styles.commentBox}>
          <TextArea
            onChange={handleCommentChange}
            value={formData.comment}
            autoSize={autoSize}
            name="comment"
            placeholder={placeholder}
            updatePosition={updatePosition}
            onFocus={() => toggleFiledFocused(true)}
            onBlur={() => toggleFiledFocused(false)}
            disabled={disabled}
            showCount={showCount}
            maxLength={maxLength}
          />
          {formData.attachments?.length > 0 ? (
            <div className={styles.attachments}>
              {formData.attachments.map((file, index) => (
                <div key={file.id || file.name} className={styles.fileBlock}>
                  <div className={styles.fileName}>
                    <Icon
                      icon="lucide:file-text"
                      width="16"
                      height="16"
                      className={styles.fileText}
                    />
                    <Tooltip title={file.name}>
                      <span className={styles.text}>{file.name}</span>
                    </Tooltip>
                    {file.size && (
                      <span className={styles.listSize}>
                        {`(${formatBytesToHumanReadableString(file.size, 0)})`}
                      </span>
                    )}
                  </div>

                  <div className={styles.cancelIcon}>
                    {getFileActionButton(file, index)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
      <div
        className={clsx(styles.actionBtn, {
          [styles.groupedBtn]: showGroup
        })}
      >
        <div className={styles.fileAttachments}>
          <FileUpload
            multipleSelection={true}
            multipleUpload={true}
            files={formData.attachments}
            setFiles={files =>
              onChange({
                ...formData,
                attachments: [...files]
              })
            }
            disabled={disabled}
            allowedFiles={allowedFiles}
          />
        </div>
        {showSendButton ? (
          <Button
            type={!isFormValid() ? 'ghost' : 'primary'}
            onClick={() => {
              onSubmit?.()
            }}
            title="send"
            disabled={!isFormValid() || disabled}
          >
            Send
          </Button>
        ) : null}
      </div>
    </div>
  )
}
